import { useCallback, useMemo } from 'react'
import { ColorRepresentation } from 'three'
import { useTheme } from '@mui/material'
import useSelectPlanViewElement from '@editor/pages/PlanView/hooks/useSelectPlanViewElement'
import { useControlStore } from '@editor/stores'
import { getSelectedPlanElement } from '@editorStores'
import {
  useGuidToCrossSection,
  useIsPlanView,
  useMouseMovedSinceMouseDown,
  usePlanElementSelection,
} from '@editorHooks'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import BatchedElementLabels from 'src/components/scene/ModelElementLabelsV2/BatchedElementLabels'
import useHiddenPlanLabelsStore from 'src/components/scene/PlanLabelController/hiddenLabelsStore'
import usePlanLabelPositionOverrides from 'src/components/scene/PlanLabelController/usePlanLabelPositionOverrides'
import { applyPlanLabelOverride } from 'src/components/scene/PlanLabelController/utils'
import { VerticalSlab2DLabelsAndIcons } from '..'
import useFloorplanLabelDescriptors from './useFloorplanLabelDescriptors'
import useLabelVisibilityFlags from './useLabelVisibilityFlags'
import usePositionLabelDescriptors from './usePositionLabelDescriptors'
import useStiffeningAnchorLabelDescriptors from './useStiffeningAnchorLabelDescriptors'

interface Props {
  visibleModel: PlanarModel
  guidToColor: Record<string, ColorRepresentation>
  guidToHidden?: Record<string, boolean>
  /** Hide the ceiling / roof position load icons (the plan PDF draws none). */
  hideLoadIcons?: boolean
}

const FloorplanModelLabels = ({
  visibleModel,
  guidToColor,
  guidToHidden = {},
  hideLoadIcons = false,
}: Props) => {
  const {
    showInnerWallLabels,
    showOuterWallLabels,
    showBeamLabels,
    showVerticalSlabLabels,
    showVerticalRoofSlabLabels,
    showPurlinLabels,
    showRipLabels,
    showLintelLabels,
    showColumnLabels,
  } = useLabelVisibilityFlags()
  const showCrossSections = useControlStore(state => state.showLabelCrossSections)
  const { scenePalette } = useTheme()

  // Derived from the whole model, so resolve it once and share it with both
  // descriptor hooks instead of letting each derive its own copy.
  const guidToCrossSection = useGuidToCrossSection()

  const positionLabelDescriptors = usePositionLabelDescriptors({
    visibleModel,
    guidToHidden,
    guidToCrossSection,
    showRipLabels,
    showLintelLabels,
    showCrossSections,
  })

  const floorplanDescriptors = useFloorplanLabelDescriptors({
    visibleModel,
    guidToColor,
    guidToHidden,
    guidToCrossSection,
    showInnerWallLabels,
    showOuterWallLabels,
    showBeamLabels,
    showVerticalSlabLabels,
    showVerticalRoofSlabLabels,
    showPurlinLabels,
    showColumnLabels,
    showCrossSections,
  })

  const stiffeningAnchorDescriptors = useStiffeningAnchorLabelDescriptors({
    visibleModel,
    guidToHidden,
    showInnerWallLabels,
    showOuterWallLabels,
  })

  const allDescriptors = useMemo(
    () => [...floorplanDescriptors, ...positionLabelDescriptors, ...stiffeningAnchorDescriptors],
    [floorplanDescriptors, positionLabelDescriptors, stiffeningAnchorDescriptors],
  )

  // Every rendered label's plan position can be moved.
  const movableLabelGuids = useMemo(
    () => new Set(allDescriptors.map(descriptor => descriptor.guid)),
    [allDescriptors],
  )

  const labelPositionOverrides = usePlanLabelPositionOverrides()

  // A label selected in `PlanLabelController` is hidden here (via
  // `useHideLabels`) for as long as its HUD is open, including while it's
  // being dragged: that component builds and draws its own movable copy in
  // its place instead, so the batch never has to re-typeset for a position
  // that updates on every pointer move.
  const hiddenLabelIdGroups = useHiddenPlanLabelsStore(state => state.hiddenIdGroups)
  const hiddenLabelGuids = useMemo(
    () => new Set(Object.values(hiddenLabelIdGroups).flat()),
    [hiddenLabelIdGroups],
  )

  const batchedDescriptors = useMemo(
    () =>
      allDescriptors.reduce((collector: ElementLabelDescriptor[], descriptor) => {
        if (hiddenLabelGuids.has(descriptor.guid)) return collector
        const override = labelPositionOverrides[descriptor.guid]
        collector.push(override ? applyPlanLabelOverride(descriptor, override) : descriptor)
        return collector
      }, []),
    [allDescriptors, labelPositionOverrides, hiddenLabelGuids],
  )

  const { selectPlanElement, clearPlanSelection } = usePlanElementSelection()
  const { mouseMoved } = useMouseMovedSinceMouseDown()
  const isPlanView = useIsPlanView()
  const selectPlanViewElement = useSelectPlanViewElement()

  const handleLabelClick = useCallback(
    (guid: string) => {
      // Ignore the click synthesized at the end of a drag (marquee / camera).
      if (mouseMoved.current) return
      selectPlanElement('element-label', guid)
      // The plan view also opens the labelled element's settings ("Bauteil" tab).
      if (isPlanView) selectPlanViewElement(guid)
    },
    [mouseMoved, selectPlanElement, isPlanView, selectPlanViewElement],
  )

  // Clicking the scene away from the labels deselects a selected label. R3F
  // reports a click as a "miss" for this group even when it lands on the label
  // HUD or ends a camera drag, so skip those; any other click away from the labels
  // (including one on a model element) deselects. Clicking another label is not a
  // miss for this group (the labels live inside it).
  const handlePointerMissed = useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0) return
      if (mouseMoved.current) return
      if (getSelectedPlanElement()?.type !== 'element-label') return
      const target = event.target
      if (target instanceof Element && target.closest('.scene-hud-button')) return
      clearPlanSelection()
    },
    [mouseMoved, clearPlanSelection],
  )

  return (
    <>
      <group onPointerMissed={handlePointerMissed}>
        <BatchedElementLabels
          descriptors={batchedDescriptors}
          occlude
          onLabelClick={handleLabelClick}
          clickableLabelGuids={movableLabelGuids}
        />
      </group>

      {visibleModel.vertical_slabs.map(slab => (
        <VerticalSlab2DLabelsAndIcons
          key={slab.guid}
          slab={slab}
          outlineColor={scenePalette.elements2d.vertical_slabs.outlineColor as string}
          hideLoadIcons={hideLoadIcons}
        />
      ))}
      {visibleModel.vertical_roof_slabs.map(slab => (
        <VerticalSlab2DLabelsAndIcons
          key={slab.guid}
          slab={slab}
          outlineColor={scenePalette.elements2d.vertical_roof_slabs.outlineColor as string}
          hideLoadIcons={hideLoadIcons}
        />
      ))}
    </>
  )
}

export default FloorplanModelLabels
